/**
 * Pure policy for global place autocomplete / geocoding.
 *
 * No network and no Google SDK here: the component and the server functions
 * feed raw payloads in and get validated, safe values out. This is what makes
 * the "never invent a location" and "never spend credits without coordinates"
 * guarantees testable across continents, alphabets and failure modes.
 */

export type PlaceSuggestion = {
  placeId: string;
  primary: string;
  secondary: string;
};

export type SelectedPlace = {
  placeId: string;
  name: string;
  city: string;
  country: string;
  countryCode: string;
  lat: number;
  lng: number;
};

export const MIN_QUERY_LENGTH = 2;
export const MAX_SUGGESTIONS = 6;

/** Scripts where a single character is already a meaningful query (CJK, etc.). */
const IDEOGRAPHIC = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af]/u;

export function normalizeQuery(raw: string | null | undefined): string {
  return (raw ?? "").replace(/\s+/gu, " ").trim();
}

/** Accent/diacritic folding used only for local comparisons, never for display. */
export function foldAccents(raw: string): string {
  return raw.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

export function shouldQueryAutocomplete(raw: string | null | undefined): boolean {
  const query = normalizeQuery(raw);
  if (!query) return false;
  if (IDEOGRAPHIC.test(query)) return true;
  return Array.from(query).length >= MIN_QUERY_LENGTH;
}

type RawPrediction = {
  placePrediction?: {
    placeId?: string | null;
    mainText?: { toString(): string } | string | null;
    secondaryText?: { toString(): string } | string | null;
    text?: { toString(): string } | string | null;
  } | null;
} | null;

function asText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object" && typeof (value as any).toString === "function") {
    const text = (value as any).toString();
    return typeof text === "string" && text !== "[object Object]" ? text.trim() : "";
  }
  return "";
}

/** Maps raw predictions to suggestions: drops junk, dedupes, caps the list. */
export function mapPredictions(raw: RawPrediction[] | null | undefined): PlaceSuggestion[] {
  const out: PlaceSuggestion[] = [];
  const seen = new Set<string>();
  for (const item of raw ?? []) {
    const prediction = item?.placePrediction;
    const placeId = (prediction?.placeId ?? "").toString().trim();
    if (!placeId || seen.has(placeId)) continue;
    const primary = asText(prediction?.mainText) || asText(prediction?.text);
    if (!primary) continue;
    seen.add(placeId);
    out.push({ placeId, primary, secondary: asText(prediction?.secondaryText) });
    if (out.length >= MAX_SUGGESTIONS) break;
  }
  return out;
}

type RawAddressComponent = {
  types?: readonly string[] | null;
  longText?: string | null;
  shortText?: string | null;
  long_name?: string | null;
  short_name?: string | null;
};

const LOCALITY_PRIORITY = [
  "locality",
  "postal_town",
  "administrative_area_level_3",
  "administrative_area_level_2",
  "administrative_area_level_1",
];

function componentText(component: RawAddressComponent | undefined, short = false): string {
  if (!component) return "";
  return ((short ? component.shortText ?? component.short_name : component.longText ?? component.long_name) ?? "").trim();
}

export type PlaceSelectionResult =
  | { ok: true; place: SelectedPlace }
  | { ok: false; reason: "missing_place_id" | "missing_coordinates" | "missing_name" };

/**
 * Validates a resolved place. A selection is only accepted when it carries a
 * place_id, a display name and finite in-range coordinates — otherwise we
 * refuse it instead of inventing a location.
 */
export function toSelectedPlace(input: {
  placeId?: string | null;
  displayName?: string | null;
  fallbackName?: string | null;
  lat?: number | null;
  lng?: number | null;
  addressComponents?: RawAddressComponent[] | null;
}): PlaceSelectionResult {
  const placeId = (input.placeId ?? "").trim();
  if (!placeId) return { ok: false, reason: "missing_place_id" };

  const lat = Number(input.lat);
  const lng = Number(input.lng);
  if (
    !Number.isFinite(lat) ||
    !Number.isFinite(lng) ||
    lat < -90 ||
    lat > 90 ||
    lng < -180 ||
    lng > 180 ||
    (lat === 0 && lng === 0)
  ) {
    return { ok: false, reason: "missing_coordinates" };
  }

  const components = input.addressComponents ?? [];
  const countryComponent = components.find((c) => c.types?.includes("country"));
  const country = componentText(countryComponent);
  const countryCode = componentText(countryComponent, true).toUpperCase();
  let city = "";
  for (const type of LOCALITY_PRIORITY) {
    city = componentText(components.find((c) => c.types?.includes(type)));
    if (city) break;
  }

  const name = (input.displayName ?? "").trim() || city || (input.fallbackName ?? "").trim();
  if (!name) return { ok: false, reason: "missing_name" };

  return {
    ok: true,
    place: { placeId, name, city: city || name, country, countryCode, lat, lng },
  };
}

/** Minimum data required before we are allowed to spend quota, credits or AI. */
export function hasUsableLocation(place: Partial<SelectedPlace> | null | undefined): boolean {
  if (!place) return false;
  const lat = Number(place.lat);
  const lng = Number(place.lng);
  const name = (place.name ?? place.city ?? "").trim();
  return (
    name.length >= 2 &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180 &&
    !(lat === 0 && lng === 0)
  );
}

export type PlacesErrorKind =
  | "missing_key"
  | "unauthorized"
  | "rate_limited"
  | "server_error"
  | "timeout"
  | "script_not_loaded"
  | "no_coordinates"
  | "empty"
  | "invalid_response"
  | "offline"
  | "unknown";

export type PlacesFailure = {
  kind: PlacesErrorKind;
  /** Portuguese, user-facing, never contains keys, URLs or payloads. */
  message: string;
  /** Whether the user can keep going by typing the destination manually. */
  allowManualEntry: boolean;
  /** Whether it is worth retrying the same request automatically. */
  retryable: boolean;
};

const MESSAGES: Record<PlacesErrorKind, string> = {
  missing_key: "A pesquisa de locais está temporariamente indisponível. Escreve o destino manualmente para continuar.",
  unauthorized: "A pesquisa de locais está temporariamente indisponível. Escreve o destino manualmente para continuar.",
  rate_limited: "Demasiadas pesquisas em pouco tempo. Aguarda alguns segundos ou escreve o destino manualmente.",
  server_error: "O serviço de mapas está com problemas. Escreve o destino manualmente para continuar.",
  timeout: "A pesquisa demorou demasiado. Tenta outra vez ou escreve o destino manualmente.",
  script_not_loaded: "Não foi possível carregar os mapas. Verifica a ligação ou escreve o destino manualmente.",
  no_coordinates: "Não conseguimos obter a localização exata desse resultado. Escolhe outro ou escreve o destino manualmente.",
  empty: "Sem resultados para essa pesquisa. Tenta outra forma de escrever ou escreve o destino manualmente.",
  invalid_response: "Recebemos uma resposta inválida do serviço de mapas. Escreve o destino manualmente para continuar.",
  offline: "Sem ligação à internet. Escreve o destino manualmente para continuar.",
  unknown: "Não foi possível pesquisar locais agora. Escreve o destino manualmente para continuar.",
};

const RETRYABLE: PlacesErrorKind[] = ["rate_limited", "server_error", "timeout", "offline"];

export function failureFor(kind: PlacesErrorKind): PlacesFailure {
  return {
    kind,
    message: MESSAGES[kind],
    allowManualEntry: true,
    retryable: RETRYABLE.includes(kind),
  };
}

/** Maps anything thrown by the loader / Places SDK / fetch into a safe failure. */
export function classifyPlacesError(error: unknown): PlacesFailure {
  const status = Number((error as any)?.status ?? (error as any)?.code);
  if (Number.isFinite(status)) {
    if (status === 401 || status === 403) return failureFor("unauthorized");
    if (status === 429) return failureFor("rate_limited");
    if (status >= 500 && status < 600) return failureFor("server_error");
  }
  const name = String((error as any)?.name ?? "");
  const raw = `${(error as any)?.message ?? error ?? ""}`;
  const text = foldAccents(raw);
  if (name === "AbortError" || name === "TimeoutError" || /timeout|timed out|demorou/.test(text)) {
    return failureFor("timeout");
  }
  if (/api key|apikey|chave|missing key|keyless/.test(text)) return failureFor("missing_key");
  if (/referernotallowed|request_denied|permission|unauthor|forbidden|invalidkey/.test(text)) {
    return failureFor("unauthorized");
  }
  if (/over_query_limit|rate limit|quota|429/.test(text)) return failureFor("rate_limited");
  if (/failed to load|script|not loaded|google is not defined|importlibrary|nao foi possivel carregar/.test(text)) {
    return failureFor("script_not_loaded");
  }
  if (/network|offline|fetch failed/.test(text)) return failureFor("offline");
  if (/unexpected token|json|invalid response|malformed/.test(text)) return failureFor("invalid_response");
  if (/5\d\d/.test(text)) return failureFor("server_error");
  return failureFor("unknown");
}

/** Classifies an HTTP response from a geocoding endpoint. */
export function classifyHttpStatus(status: number): PlacesFailure | null {
  if (status >= 200 && status < 300) return null;
  if (status === 401 || status === 403) return failureFor("unauthorized");
  if (status === 429) return failureFor("rate_limited");
  if (status >= 500) return failureFor("server_error");
  return failureFor("unknown");
}
